// Zustand Enhanced Store - Layer 4: UI Enhancement Features
// Production-ready enhanced state with pure functions and zero side effects

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { VehicleEnhanced } from '../types/vehicle-enhanced';

interface VehicleFilters {
  capacity: 'all' | 'high' | 'medium' | 'low';
  skills: string[];
  vroomCompatible: boolean;
  search: string;
}

interface VehicleEnhancedState {
  // Selection state
  selectedVehicles: Set<number>;
  
  // Filter state (enhanced beyond URL filters)
  enhancedFilters: VehicleFilters;
  
  // View preferences
  viewMode: 'grid' | 'list' | 'table';
  sortBy: 'name' | 'capacity' | 'skills' | 'updated' | 'vroom_score';
  sortOrder: 'asc' | 'desc';
  
  // Bulk action state
  bulkActionMode: boolean;
  bulkActionType: 'edit' | 'delete' | 'export' | null;
  
  // UI enhancement state
  showAdvancedFilters: boolean;
  expandedVehicleIds: Set<number>;
  
  // Pure action functions (no side effects)
  actions: {
    // Selection actions
    selectVehicle: (id: number) => void;
    deselectVehicle: (id: number) => void;
    toggleVehicleSelection: (id: number) => void;
    selectAll: (vehicleIds: number[]) => void;
    clearSelection: () => void;
    
    // Filter actions  
    updateEnhancedFilters: (filters: Partial<VehicleFilters>) => void;
    resetEnhancedFilters: () => void;
    
    // View actions
    setViewMode: (mode: 'grid' | 'list' | 'table') => void;
    setSorting: (sortBy: string, order?: 'asc' | 'desc') => void;
    
    // Bulk action management
    enterBulkActionMode: (actionType: 'edit' | 'delete' | 'export') => void;
    exitBulkActionMode: () => void;
    
    // UI state management
    toggleAdvancedFilters: () => void;
    expandVehicle: (id: number) => void;
    collapseVehicle: (id: number) => void;
    toggleVehicleExpansion: (id: number) => void;
    
    // Pure enhancer functions (computational only)
    filterVehicles: (vehicles: VehicleEnhanced[]) => VehicleEnhanced[];
    sortVehicles: (vehicles: VehicleEnhanced[]) => VehicleEnhanced[];
    searchVehicles: (vehicles: VehicleEnhanced[], searchTerm: string) => VehicleEnhanced[];
    getSelectionStats: (vehicles: VehicleEnhanced[]) => {
      selectedCount: number;
      totalCapacity: number;
      avgVroomScore: number;
      skillCount: number;
    };
  };
}

const initialEnhancedFilters: VehicleFilters = {
  capacity: 'all',
  skills: [],
  vroomCompatible: false,
  search: ''
};

/**
 * Vehicle Enhanced Store - Zustand implementation
 * Layer 4: Pure enhancement functions without side effects
 */
export const useVehicleEnhancedStore = create<VehicleEnhancedState>()(
  devtools(
    (set, get) => ({
      // Initial state
      selectedVehicles: new Set(),
      enhancedFilters: initialEnhancedFilters,
      viewMode: 'grid',
      sortBy: 'name',
      sortOrder: 'asc',
      bulkActionMode: false,
      bulkActionType: null,
      showAdvancedFilters: false,
      expandedVehicleIds: new Set(),
      
      actions: {
        // Selection management
        selectVehicle: (id) =>
          set(state => ({
            selectedVehicles: new Set([...state.selectedVehicles, id])
          }), false, 'selectVehicle'),
        
        deselectVehicle: (id) =>
          set(state => {
            const newSelection = new Set(state.selectedVehicles);
            newSelection.delete(id);
            return { selectedVehicles: newSelection };
          }, false, 'deselectVehicle'),
        
        toggleVehicleSelection: (id) =>
          set(state => {
            const newSelection = new Set(state.selectedVehicles);
            if (newSelection.has(id)) {
              newSelection.delete(id);
            } else {
              newSelection.add(id);
            }
            return { selectedVehicles: newSelection };
          }, false, 'toggleVehicleSelection'),
        
        selectAll: (vehicleIds) =>
          set({ 
            selectedVehicles: new Set(vehicleIds) 
          }, false, 'selectAll'),
        
        clearSelection: () =>
          set({ 
            selectedVehicles: new Set(),
            bulkActionMode: false,
            bulkActionType: null
          }, false, 'clearSelection'),
        
        // Enhanced filter management
        updateEnhancedFilters: (newFilters) =>
          set(state => ({
            enhancedFilters: { ...state.enhancedFilters, ...newFilters }
          }), false, 'updateEnhancedFilters'),
        
        resetEnhancedFilters: () =>
          set({ 
            enhancedFilters: initialEnhancedFilters 
          }, false, 'resetEnhancedFilters'),
        
        // View management
        setViewMode: (mode) =>
          set({ viewMode: mode }, false, 'setViewMode'),
        
        setSorting: (sortBy, order = 'asc') =>
          set({ sortBy, sortOrder: order }, false, 'setSorting'),
        
        // Bulk action management
        enterBulkActionMode: (actionType) =>
          set({ 
            bulkActionMode: true, 
            bulkActionType: actionType 
          }, false, 'enterBulkActionMode'),
        
        exitBulkActionMode: () =>
          set({ 
            bulkActionMode: false, 
            bulkActionType: null,
            selectedVehicles: new Set()
          }, false, 'exitBulkActionMode'),
        
        // UI state management
        toggleAdvancedFilters: () =>
          set(state => ({ 
            showAdvancedFilters: !state.showAdvancedFilters 
          }), false, 'toggleAdvancedFilters'),
        
        expandVehicle: (id) =>
          set(state => ({
            expandedVehicleIds: new Set([...state.expandedVehicleIds, id])
          }), false, 'expandVehicle'),
        
        collapseVehicle: (id) =>
          set(state => {
            const newExpanded = new Set(state.expandedVehicleIds);
            newExpanded.delete(id);
            return { expandedVehicleIds: newExpanded };
          }, false, 'collapseVehicle'),
        
        toggleVehicleExpansion: (id) =>
          set(state => {
            const newExpanded = new Set(state.expandedVehicleIds);
            if (newExpanded.has(id)) {
              newExpanded.delete(id);
            } else {
              newExpanded.add(id);
            }
            return { expandedVehicleIds: newExpanded };
          }, false, 'toggleVehicleExpansion'),
        
        // Pure computational functions (no state mutations)
        filterVehicles: (vehicles) => {
          const { enhancedFilters } = get();
          
          return vehicles.filter(vehicle => {
            // Search filter
            if (enhancedFilters.search) {
              const searchLower = enhancedFilters.search.toLowerCase();
              const matchesSearch = [
                vehicle.name,
                vehicle.description,
                vehicle.plate_number,
                ...(vehicle.skills_detailed?.map(s => s.name) || [])
              ].some(field => 
                field?.toLowerCase().includes(searchLower)
              );
              if (!matchesSearch) return false;
            }
            
            // Capacity filter
            if (enhancedFilters.capacity !== 'all') {
              const capacity = vehicle.capacity_array?.[0] || 0;
              const threshold = {
                high: 1000,
                medium: 500,
                low: 100
              }[enhancedFilters.capacity];
              if (capacity < threshold) return false;
            }
            
            // Skills filter
            if (enhancedFilters.skills.length > 0) {
              const vehicleSkills = vehicle.skills_detailed?.map(s => s.name) || [];
              if (!enhancedFilters.skills.some(skill => vehicleSkills.includes(skill))) {
                return false;
              }
            }
            
            // VROOM compatibility filter
            if (enhancedFilters.vroomCompatible && !vehicle.vroom_compatible?.overall_compatible) {
              return false;
            }
            
            return true;
          });
        },
        
        sortVehicles: (vehicles) => {
          const { sortBy, sortOrder } = get();
          
          return [...vehicles].sort((a, b) => {
            let comparison = 0;
            
            switch (sortBy) {
              case 'name':
                comparison = (a.name || '').localeCompare(b.name || '');
                break;
              case 'capacity':
                comparison = (a.capacity_array?.[0] || 0) - (b.capacity_array?.[0] || 0);
                break;
              case 'skills':
                comparison = (a.skills_detailed?.length || 0) - (b.skills_detailed?.length || 0);
                break;
              case 'updated':
                comparison = new Date(a.updated_at || 0).getTime() - 
                           new Date(b.updated_at || 0).getTime();
                break;
              case 'vroom_score':
                const scoreA = a.vroom_compatible?.compatibility_score || 0;
                const scoreB = b.vroom_compatible?.compatibility_score || 0;
                comparison = scoreA - scoreB;
                break;
              default:
                comparison = 0;
            }
            
            return sortOrder === 'desc' ? -comparison : comparison;
          });
        },
        
        searchVehicles: (vehicles, searchTerm) => {
          if (!searchTerm.trim()) return vehicles;
          
          const searchLower = searchTerm.toLowerCase();
          return vehicles.filter(vehicle => 
            [
              vehicle.name,
              vehicle.description,
              vehicle.plate_number,
              vehicle.vehicle_type,
              ...(vehicle.skills_detailed?.map(s => s.name) || [])
            ].some(field => 
              field?.toLowerCase().includes(searchLower)
            )
          );
        },
        
        getSelectionStats: (vehicles) => {
          const { selectedVehicles } = get();
          const selectedVehicleData = vehicles.filter(v => selectedVehicles.has(v.id));
          
          return {
            selectedCount: selectedVehicleData.length,
            totalCapacity: selectedVehicleData.reduce(
              (sum, v) => sum + (v.capacity_array?.[0] || 0), 0
            ),
            avgVroomScore: selectedVehicleData.length > 0
              ? selectedVehicleData.reduce(
                  (sum, v) => sum + (v.vroom_compatible?.compatibility_score || 0), 0
                ) / selectedVehicleData.length
              : 0,
            skillCount: new Set(
              selectedVehicleData.flatMap(v => 
                v.skills_detailed?.map(s => s.name) || []
              )
            ).size
          };
        }
      }
    }),
    { name: 'vehicle-enhanced-store' }
  )
);

/**
 * Selector hooks for optimized component subscriptions
 * Components can subscribe to specific store slices
 */
export const useVehicleSelection = () => 
  useVehicleEnhancedStore(state => ({
    selectedVehicles: state.selectedVehicles,
    bulkActionMode: state.bulkActionMode,
    bulkActionType: state.bulkActionType,
    selectVehicle: state.actions.selectVehicle,
    deselectVehicle: state.actions.deselectVehicle,
    toggleVehicleSelection: state.actions.toggleVehicleSelection,
    selectAll: state.actions.selectAll,
    clearSelection: state.actions.clearSelection
  }));

export const useVehicleFilters = () =>
  useVehicleEnhancedStore(state => ({
    enhancedFilters: state.enhancedFilters,
    showAdvancedFilters: state.showAdvancedFilters,
    updateEnhancedFilters: state.actions.updateEnhancedFilters,
    resetEnhancedFilters: state.actions.resetEnhancedFilters,
    toggleAdvancedFilters: state.actions.toggleAdvancedFilters,
    filterVehicles: state.actions.filterVehicles
  }));

export const useVehicleView = () =>
  useVehicleEnhancedStore(state => ({
    viewMode: state.viewMode,
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
    expandedVehicleIds: state.expandedVehicleIds,
    setViewMode: state.actions.setViewMode,
    setSorting: state.actions.setSorting,
    sortVehicles: state.actions.sortVehicles
  }));